import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { en } from "./translations/en";
import { he } from "./translations/he";

export type Locale = "en" | "he";

const translations: Record<Locale, Record<string, string>> = { en, he };

const LOCALE_KEY = "nigunim:locale";

interface I18nContext {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isRTL: boolean;
}

const Ctx = createContext<I18nContext>({
  locale: "he",
  setLocale: () => {},
  t: (k) => k,
  isRTL: true,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === "undefined") return "he";
    const stored = localStorage.getItem(LOCALE_KEY) as Locale | null;
    if (stored === "he" || stored === "en") return stored;
    return "he";
  });

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    if (typeof window !== "undefined") {
      localStorage.setItem(LOCALE_KEY, l);
    }
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "he" ? "rtl" : "ltr";
  }, [locale]);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    let str = translations[locale]?.[key] ?? translations.he[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        str = str.replaceAll(`{${k}}`, String(v));
      }
    }
    return str;
  }, [locale]);

  const isRTL = locale === "he";

  return (
    <Ctx.Provider value={{ locale, setLocale, t, isRTL }}>
      {children}
    </Ctx.Provider>
  );
}

export function useLocale() {
  return useContext(Ctx);
}

// Alias for plan-naming consistency; both names work.
export const useI18n = useLocale;
